import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { LightDomReparentController } from '@/controllers/LightDomReparentController.js';
import { dispatchFilterEvent, FILTER_EVENTS, FILTER_PRESENTATIONS } from '@/components/filters/constants.js';
import styles from './filter-group.scss?inline';

const BUCKETS = Object.freeze({
    OPTIONS: 'options',
    APPLY: 'apply',
    RESET: 'reset',
});

/**
 * One filter axis. It owns presentation and draft checkbox UI state; consumers
 * keep business "applied" state via filter-apply / filter-reset events.
 * Option changes also commit and fire filter-change with the toggled checkbox.
 *
 * @fires filter-change - detail: { name, option, value, checked, originalEvent }
 * @fires filter-apply - detail: { name }
 * @fires filter-reset - detail: { name }
 * @fires filter-open - detail: { name }
 * @fires filter-close - detail: { name }
 * @fires filter-presentation-change - detail: { name, presentation }
 */
export default class SotbitFilterGroup extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        label: { type: String },
        name: { type: String },
        count: { type: Number, reflect: true },
        presentation: { type: String, reflect: true },
    };

    constructor() {
        super();
        this.label = '';
        this.name = '';
        this.count = 0;
        this.presentation = FILTER_PRESENTATIONS.DROPDOWN;
        /** @type {Set<string>} last committed option values (Apply / Reset) */
        this._committedValues = new Set();

        this._dropdownRef = createRef();
        this._panelRef = createRef();
        this._actionsRef = createRef();
        this._nodes = new LightDomReparentController(this);
        this._nodes.listen(BUCKETS.OPTIONS, 'change', this._onOptionChange);
        this._nodes.listen(BUCKETS.APPLY, 'click', this._onApplyClick);
        this._nodes.listen(BUCKETS.RESET, 'click', this._onResetClick);
    }

    connectedCallback() {
        super.connectedCallback();
        this._nodes.connect();
    }

    disconnectedCallback() {
        void this.close();
        this._nodes.disconnect();
        super.disconnectedCallback();
    }

    /**
     * Closes the internal dropdown through its public API.
     *
     * @returns {Promise<void>}
     */
    async close() {
        await this._dropdownRef.value?.hide?.();
    }

    /**
     * Width the dropdown trigger needs so its label is not clipped.
     * Compares the host box with the anchor, which can be wider than the row.
     *
     * @returns {number}
     */
    getTriggerWidth() {
        const hostWidth = Math.ceil(this.getBoundingClientRect().width);
        const anchor = this._dropdownRef.value?.renderRoot?.querySelector('.dropdown-anchor');
        const anchorWidth = anchor ? Math.ceil(anchor.getBoundingClientRect().width) : 0;

        return Math.max(hostWidth, anchorWidth);
    }

    /**
     * Option nodes tracked by this group (including teleported ones).
     *
     * @returns {Element[]}
     */
    getOptions() {
        return this._nodes.get(BUCKETS.OPTIONS);
    }

    /**
     * Current checked option values (reads tracked nodes, including teleported ones).
     *
     * @returns {string[]}
     */
    getSelectedValues() {
        return this.getOptions()
            .filter((node) => node && 'checked' in node && node.checked)
            .map((node) => String(node.value));
    }

    /**
     * Snapshot current option checked values as committed (after Apply).
     */
    commitOptions() {
        this._committedValues = new Set(this.getSelectedValues());
    }

    /**
     * Clear committed values (after Reset).
     */
    clearCommittedOptions() {
        this._committedValues = new Set();
    }

    /**
     * Restore option checked state to the last committed selection.
     */
    restoreCommittedOptions() {
        this._nodes.get(BUCKETS.OPTIONS).forEach((node) => {
            if (!node || !('checked' in node)) {
                return;
            }
            const shouldCheck = this._committedValues.has(String(node.value));
            if (node.checked !== shouldCheck) {
                node.checked = shouldCheck;
            }
        });
    }

    _captureSlot(bucket, slot) {
        this._nodes.capture(bucket, slot.assignedElements({ flatten: true }));
        this._distribute();
    }

    _onOptionsSlotChange = ({ currentTarget }) => {
        this._captureSlot(BUCKETS.OPTIONS, currentTarget);
    };

    _onApplySlotChange = ({ currentTarget }) => {
        this._captureSlot(BUCKETS.APPLY, currentTarget);
    };

    _onResetSlotChange = ({ currentTarget }) => {
        this._captureSlot(BUCKETS.RESET, currentTarget);
    };

    /**
     * Moves options into `sotbit-filter-panel` and actions into dropdown footer.
     */
    _distribute() {
        if (this.presentation !== FILTER_PRESENTATIONS.DROPDOWN) {
            return;
        }

        const panel = this._panelRef.value;
        if (!panel) {
            return;
        }

        this._nodes.move(BUCKETS.OPTIONS, panel);

        const actions = this._actionsRef.value;
        if (actions) {
            this._nodes.move(BUCKETS.APPLY, actions);
            this._nodes.move(BUCKETS.RESET, actions);
            actions.hidden =
                this._nodes.get(BUCKETS.APPLY).length + this._nodes.get(BUCKETS.RESET).length === 0;
        }
    }

    _onDropdownOpen = () => {
        this.restoreCommittedOptions();
        dispatchFilterEvent(this, FILTER_EVENTS.OPEN, { name: this.name || null });
    };

    _onDropdownClose = () => {
        dispatchFilterEvent(this, FILTER_EVENTS.CLOSE, { name: this.name || null });
    };

    /**
     * Keep committed UI in sync and notify ancestors (filters-collapse / consumers).
     * Listener is attached to option nodes, so teleport does not break it.
     *
     * @param {Event} event
     */
    _onOptionChange = (event) => {
        const option = event.currentTarget;
        this.commitOptions();
        dispatchFilterEvent(this, FILTER_EVENTS.CHANGE, {
            name: this.name || null,
            option,
            value: option?.value != null ? String(option.value) : '',
            checked: Boolean(option?.checked),
            originalEvent: event,
        });
    };

    _onApplyClick = (event) => {
        event.preventDefault();
        const accepted = dispatchFilterEvent(this, FILTER_EVENTS.APPLY, {
            name: this.name || null,
        });

        if (accepted) {
            this.commitOptions();
            void this.close();
        }
    };

    _onResetClick = (event) => {
        event.preventDefault();
        const accepted = dispatchFilterEvent(this, FILTER_EVENTS.RESET, {
            name: this.name || null,
        });

        if (accepted) {
            this.clearCommittedOptions();
            this.restoreCommittedOptions();
            void this.close();
        }
    };

    _renderActionCaptureSlots() {
        return html`
            <div class="filter-group__capture" hidden>
                <slot name="apply" @slotchange=${this._onApplySlotChange}></slot>
                <slot name="reset" @slotchange=${this._onResetSlotChange}></slot>
            </div>
        `;
    }

    _renderDropdown() {
        const accessibleLabel = this.count > 0 ? `${this.label} (${this.count})` : this.label;

        return html`
            <sotbit-dropdown
                ${ref(this._dropdownRef)}
                class="filter-group__dropdown"
                part="filter-group__dropdown"
                variant="dropdown-select"
                max-content
                max-height="450px"
                modal-class="filter-group-modal"
                hide-on-select="false"
                disable-close-on-resize
                use-teleport
                @open=${this._onDropdownOpen}
                @close=${this._onDropdownClose}
            >
                <sotbit-button-dropdown slot="anchor" flat background aria-label=${accessibleLabel || nothing}>
                    <span
                        slot="content"
                        class="filter-group__button-title"
                        part="filter-group__button-title"
                        data-count=${this.count > 0 ? this.count : nothing}
                    >
                        ${this.label}
                    </span>
                    <sotbit-icon slot="icon-right" icon="dropdown" variant="simple"></sotbit-icon>
                </sotbit-button-dropdown>
                <sotbit-filter-panel ${ref(this._panelRef)} variant="axis"></sotbit-filter-panel>
                <div
                    slot="footer"
                    class="filter-group__actions"
                    part="filter-group__actions"
                    ${ref(this._actionsRef)}
                    hidden
                ></div>
            </sotbit-dropdown>
            <div class="filter-group__capture" hidden>
                <slot @slotchange=${this._onOptionsSlotChange}></slot>
                <slot name="apply" @slotchange=${this._onApplySlotChange}></slot>
                <slot name="reset" @slotchange=${this._onResetSlotChange}></slot>
            </div>
        `;
    }

    _renderSection() {
        return html`
            <section class="filter-group__section" part="filter-group__section" aria-label=${this.label || nothing}>
                ${this.label
                    ? html`<div class="filter-group__title" part="filter-group__title">${this.label}</div>`
                    : nothing}
                <div class="filter-group__list" part="filter-group__list">
                    <slot @slotchange=${this._onOptionsSlotChange}></slot>
                </div>
            </section>
            ${this._renderActionCaptureSlots()}
        `;
    }

    willUpdate(changedProperties) {
        if (changedProperties.has('presentation')) {
            this._nodes.restoreAll();
        }
    }

    updated(changedProperties) {
        if (changedProperties.has('presentation')) {
            const previous = changedProperties.get('presentation');
            if (previous !== undefined) {
                dispatchFilterEvent(this, FILTER_EVENTS.PRESENTATION_CHANGE, {
                    name: this.name || null,
                    presentation: this.presentation,
                });
            }
            this._distribute();
        }
    }

    render() {
        return this.presentation === FILTER_PRESENTATIONS.SECTION ? this._renderSection() : this._renderDropdown();
    }
}
